import { useState, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { compressImageDataUrl } from '../lib/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Camera, Upload, Check, X, Image } from 'lucide-react';
import { toast } from 'sonner';

export default function MeetingPhotosPage() {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [branchFilter, setBranchFilter] = useState('all');
  const [branches, setBranches] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  // Lightbox viewer for clicked photo (lazily fetches the heavy base64 only on click)
  const [viewer, setViewer] = useState(null);

  const handleView = async (p) => {
    setViewer({ ...p, photo_base64: null });
    try {
      const { data } = await api.get(`/meeting-photos/${p.id}`);
      setViewer(data);
    } catch (err) { toast.error(formatApiError(err)); setViewer(null); }
  };

  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  const isCorporate = ['super_admin', 'management'].includes(user?.system_role);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { month };
        if (isCorporate && branchFilter && branchFilter !== 'all') params.branch_id = branchFilter;
        const [pRes] = await Promise.all([
          api.get('/meeting-photos', { params }),
        ]);
        setPhotos(pRes.data);
        if (isCorporate) {
          const rRes = await api.get('/meeting-photos/report/summary', { params: { month } });
          setReport(rRes.data);
          if (branches.length === 0) {
            const bRes = await api.get('/branches');
            setBranches(bRes.data || []);
          }
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [month, branchFilter, isCorporate]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Photo too large (>25MB)'); return; }
    try {
      // Auto-compress before storing — keeps payload < ~150KB so the user
      // never has to manually shrink images on their phone.
      const compressed = await compressImageDataUrl(file);
      setPreview(compressed);
      setPhotoData(compressed);
    } catch (err) {
      toast.error('Could not read photo. Try another image.');
    }
  };

  const handleUpload = async () => {
    if (!photoData) { toast.error('Select a photo first'); return; }
    setUploading(true);
    try {
      await api.post('/meeting-photos', { photo_base64: photoData, notes, date: new Date().toISOString().slice(0, 10) });
      toast.success('Meeting photo uploaded');
      setPreview(null); setPhotoData(null); setNotes('');
      const pRes = await api.get('/meeting-photos', { params: { month } });
      setPhotos(pRes.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const todayUploaded = photos.some(p => p.date === new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="meeting-photos-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Daily Meeting Photos</h1>
          <p className="text-neutral-500 text-sm mt-1">Store/team meeting documentation</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {isCorporate && (
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="w-full sm:w-56 rounded-sm bg-white h-10" data-testid="meeting-branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="meeting-month-filter" />
        </div>
      </div>

      {/* Upload Section (Branch Manager) */}
      {user?.system_role === 'branch_manager' && (
        <Card className={`border shadow-sm rounded-md ${todayUploaded ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
              <Camera className="h-4 w-4 text-red-600" />
              {todayUploaded ? "Today's Photo Uploaded" : "Upload Today's Meeting Photo"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {todayUploaded ? (
              <p className="text-sm text-emerald-700 flex items-center gap-1.5"><Check className="h-4 w-4" /> Already uploaded for today</p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="flex-1 border-2 border-dashed border-neutral-300 rounded-sm p-4 text-center cursor-pointer hover:border-red-400 transition-colors"
                    data-testid="meeting-photo-input-label">
                    <input type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" data-testid="meeting-photo-input" />
                    {preview ? (
                      <img src={preview} alt="Preview" className="max-h-32 mx-auto rounded" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-neutral-400">
                        <Upload className="h-6 w-6" />
                        <p className="text-xs">Tap to take or select photo</p>
                      </div>
                    )}
                  </label>
                  <div className="flex flex-col gap-2 sm:w-48">
                    <Textarea placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)}
                      className="rounded-sm text-sm flex-1" rows={3} data-testid="meeting-notes-input" />
                    <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={handleUpload}
                      disabled={uploading || !photoData} data-testid="meeting-upload-button">
                      <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photo Log */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-neutral-700">Photo Log - {month}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {photos.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">No photos uploaded for this month</p>
          ) : (
            <div className="space-y-2">
              {photos.map(p => (
                <div key={p.id} className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0 gap-3">
                  <button type="button"
                    onClick={() => handleView(p)}
                    className="flex items-center gap-3 flex-1 text-left hover:bg-neutral-50 rounded-sm px-1 py-1 transition-colors"
                    data-testid={`view-meeting-photo-${p.id}`}>
                    <div className="w-12 h-12 bg-neutral-100 rounded flex items-center justify-center group-hover:ring-2 ring-red-300">
                      <Image className="h-5 w-5 text-neutral-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-800 truncate">{p.date}{p.branch_name ? <span className="text-xs text-neutral-400 font-normal"> · {p.branch_name}</span> : null}</p>
                      <p className="text-xs text-neutral-500 truncate">By {p.uploaded_by_name} {p.notes ? `· ${p.notes}` : ''}</p>
                    </div>
                  </button>
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs whitespace-nowrap">View</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photo viewer dialog (lightbox) */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) setViewer(null); }}>
        <DialogContent className="max-w-3xl" data-testid="meeting-photo-viewer">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {viewer?.date} {viewer?.branch_name ? <span className="text-xs text-neutral-400 font-normal">· {viewer.branch_name}</span> : null}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Uploaded by {viewer?.uploaded_by_name}{viewer?.notes ? ` · ${viewer.notes}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center bg-neutral-50 rounded-sm p-2">
            {viewer?.photo_base64 ? (
              <img src={viewer.photo_base64} alt={viewer.date} className="max-h-[70vh] w-auto rounded-sm shadow-sm" />
            ) : (
              <div className="py-12 text-sm text-neutral-400">Loading photo…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Corporate Report */}
      {isCorporate && report.length > 0 && (
        <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-neutral-700">Branch Compliance Report</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Branch</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Uploaded</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Missing</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.map(r => (
                  <TableRow key={r.branch_id}>
                    <TableCell className="text-sm font-medium">{r.branch_name}</TableCell>
                    <TableCell className="text-sm">{r.uploaded_count}/{r.total_days}</TableCell>
                    <TableCell className="text-sm text-red-600 font-semibold">{r.missing_count}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${r.missing_count === 0 ? 'bg-emerald-100 text-emerald-700' : r.uploaded_count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {r.missing_count === 0 ? 'Complete' : r.uploaded_count > 0 ? 'Partial' : 'No uploads'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
